import React, { useCallback, useMemo, useState } from 'react';
import { Box, Card, CardActionArea, CardContent, Chip, CircularProgress, Grid, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useTranslation } from 'react-i18next';
import { useCatalog } from './apps-catalog';
import { useUserAppsContext } from './user-apps-context';
import useHoverNavigation from './use-hover-navigation';
import { hoverHighlightSx } from './hover-style';
import AppsActionBarWithSettings from './apps-action-bar-with-settings';

export default function Store() {
    const { categories, loading } = useCatalog();
    const allApps = useMemo(() => categories.flatMap(c => c.apps), [categories]);
    const { t } = useTranslation();
    const { activatedApps, toggleApp } = useUserAppsContext();
    const [actionBarActions, setActionBarActions] = useState([]);

    const itemCount = allApps.length + actionBarActions.length;

    const onSelect = useCallback((index) => {
        if (index < allApps.length) {
            // App card clicked
            const app = allApps[index];
            if (app?.route) toggleApp(app.route);
        } else {
            // Action bar item clicked
            const actionIndex = index - allApps.length;
            if (actionIndex >= 0 && actionIndex < actionBarActions.length) {
                actionBarActions[actionIndex].onClick();
            }
        }
    }, [allApps, toggleApp, actionBarActions]);

    const { active, activeIndex } = useHoverNavigation(itemCount, onSelect);

    let flatIndex = 0;

    return (
        <Box sx={{ p: 0, position: 'relative', minHeight: '100%' }}>
            {/* Action bar */}
            <AppsActionBarWithSettings
                customActions={[]}
                helperActions={[]}
                activeIndex={activeIndex - allApps.length}
                active={active && activeIndex >= allApps.length}
                onActionsChange={setActionBarActions}
                flex="0 0 auto"
            />

            <Typography variant="h4" gutterBottom>
                {t('APPLICATIONS.STORE_TITLE')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
                {t('APPLICATIONS.BETA_NOTICE')}
            </Typography>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {categories.map((category) => {
                const standardApps = category.apps.filter(a => !a.ai);
                const aiApps = category.apps.filter(a => a.ai);

                return (
                    <Box key={category.labelKey} sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            {t(category.labelKey)}
                        </Typography>
                        {standardApps.length > 0 && (
                            <Grid container spacing={3}>
                                {standardApps.map((app) => {
                                    const index = flatIndex++;
                                    return renderAppCard(app, index, activatedApps, active, activeIndex, t, toggleApp);
                                })}
                            </Grid>
                        )}
                        {aiApps.length > 0 && (
                            <Grid container spacing={3} sx={{ mt: standardApps.length > 0 ? 1 : 0 }}>
                                {aiApps.map((app) => {
                                    const index = flatIndex++;
                                    return renderAppCard(app, index, activatedApps, active, activeIndex, t, toggleApp);
                                })}
                            </Grid>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

function renderAppCard(app, index, activatedApps, active, activeIndex, t, toggleApp) {
    const Icon = app.icon;
    const comingSoon = !app.route;
    const isActivated = !comingSoon && activatedApps.has(app.route);
    const isHovered = active && index === activeIndex;
    const cardSx = {
        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
        ...((!isActivated && !comingSoon) && { opacity: 0.6 }),
        ...(isHovered && hoverHighlightSx),
    };

    const cardContent = (
        <CardContent sx={{ textAlign: 'center' }}>
            <Icon sx={{ fontSize: 48, mb: 1, color: comingSoon ? 'text.secondary' : isActivated ? 'primary.main' : 'text.secondary' }} />
            <Typography variant="h6">
                {t(app.titleKey)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t(app.descKey)}
            </Typography>
            {comingSoon ? (
                <Chip
                    icon={<HourglassEmptyIcon />}
                    label={t('APPLICATIONS.COMING_SOON')}
                    color="warning"
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                />
            ) : (
                <Chip
                    icon={isActivated ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                    label={t(isActivated ? 'APPLICATIONS.ACTIVATED' : 'APPLICATIONS.DEACTIVATED')}
                    color={isActivated ? 'success' : 'default'}
                    variant={isActivated ? 'filled' : 'outlined'}
                    size="small"
                    sx={{ mt: 2 }}
                />
            )}
        </CardContent>
    );

    return (
        <Grid item xs={12} sm={6} md={4} key={app.titleKey}>
            <Card sx={cardSx}>
                {comingSoon ? cardContent : (
                    <CardActionArea onClick={() => toggleApp(app.route)}>
                        {cardContent}
                    </CardActionArea>
                )}
            </Card>
        </Grid>
    );
}
